import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [permissions, setPermissions] = useState(null); // null = unknown (fail-open); [] = none; ["*"] = all
  const [status, setStatus] = useState("loading"); // loading | authed | guest
  // Issue 2 — generation guard: only the LATEST fetchMe may write auth state, so a slow
  // stale call (e.g. the initial guest check on a cold start) can't clobber a fresh login.
  const genRef = useRef(0);

  const fetchMe = async () => {
    const gen = ++genRef.current;
    try {
      // Fetch identity + permissions in parallel (independent) to cut bootstrap latency.
      const [meRes, permRes] = await Promise.allSettled([
        api.get("/auth/me"),
        api.get("/security/me/permissions"),
      ]);
      if (gen !== genRef.current) return false; // superseded by a newer auth check
      if (meRes.status !== "fulfilled") throw meRes.reason || new Error("auth/me failed");
      setUser(meRes.value.data.user);
      setTenant(meRes.value.data.tenant);
      // unknown permissions → fail-open (backend still enforces on the API)
      setPermissions(permRes.status === "fulfilled" ? (permRes.value.data?.permissions || []) : null);
      setStatus("authed");
      return true;
    } catch {
      if (gen !== genRef.current) return false; // stale failure — do not clobber newer state
      setUser(null);
      setTenant(null);
      setPermissions(null);
      setStatus("guest");
      return false;
    }
  };

  useEffect(() => {
    // /kiosk is a public, tokenless self-checkout surface (uses its own kioskApi) — skip the
    // app auth bootstrap so it doesn't fire /auth/me → 401 probes on this screen.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/kiosk")) {
      setStatus("guest");
      return;
    }
    // Skip the guaranteed-401 bootstrap probe (/auth/me + /security/me/permissions) for anonymous
    // visitors on public pages (no token in localStorage) — they would only 401 and spam the
    // console. We still probe on in-app routes, and on public pages when a token exists (a returning
    // user is detected and can be redirected in). Login/register set the token + fire an event
    // that triggers the probe below, so same-tab sign-in still hydrates immediately.
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    const publicPage = path === "/"
      || ["/login", "/register", "/legal", "/vendor-portal"].some((p) => path.startsWith(p));
    const hasToken = typeof window !== "undefined" && !!localStorage.getItem("reason_token");
    if (!hasToken && publicPage) { setStatus("guest"); return; }
    fetchMe();
  }, []);

  const login = async (email, password) => {
    let data;
    try {
      ({ data } = await api.post("/auth/login", { email, password }));
    } catch (err) {
      // E1C R2 — single active session: another device is signed in. Surface the choice to the caller.
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.code === "ACTIVE_SESSION_EXISTS") {
        return { sessionConflict: true, resetToken: d.reset_token };
      }
      throw err;
    }
    if (data.mfa_required) {
      return { mfaRequired: true, challengeToken: data.challenge_token };
    }
    if (data.access_token) { localStorage.setItem("reason_token", data.access_token); window.dispatchEvent(new Event("reason-auth-changed")); }
    // Perceived-latency cut: the login response already carries the user, so authenticate the UI
    // immediately (the dashboard route + its lazy chunk start loading right away) and hydrate
    // tenant + permissions in the BACKGROUND instead of blocking on a second round-trip. Permissions
    // stay null (fail-open; backend still enforces) until they arrive a few ms later.
    if (data.user) {
      const gen = ++genRef.current;
      setUser(data.user);
      setStatus("authed");
      (async () => {
        try {
          const [meRes, permRes] = await Promise.allSettled([
            api.get("/auth/me"),
            api.get("/security/me/permissions"),
          ]);
          if (gen !== genRef.current) return;
          if (meRes.status === "fulfilled") {
            setUser(meRes.value.data.user);
            setTenant(meRes.value.data.tenant);
          }
          setPermissions(permRes.status === "fulfilled" ? (permRes.value.data?.permissions || []) : null);
        } catch { /* backend enforces on each API call */ }
      })();
      return { user: data.user };
    }
    const ok = await fetchMe();
    if (!ok) throw new Error("We couldn't establish your session. Please try signing in again.");
    return { user: data.user };
  };

  const verifyMfaLogin = async (challengeToken, code) => {
    let data;
    try {
      ({ data } = await api.post("/auth/mfa/verify-login", { challenge_token: challengeToken, code }));
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.code === "ACTIVE_SESSION_EXISTS") {
        return { sessionConflict: true, resetToken: d.reset_token };
      }
      throw err;
    }
    if (data.access_token) { localStorage.setItem("reason_token", data.access_token); window.dispatchEvent(new Event("reason-auth-changed")); }
    const ok = await fetchMe();
    if (!ok) throw new Error("We couldn't establish your session. Please try signing in again.");
    return { user: data.user };
  };

  // E1C R2 — "Reset Current Session & Login Here": consumes the one-time reset challenge, terminates the
  // previous session (its tokens die immediately) and signs in here with new session-bound tokens.
  const resetSession = async (resetToken) => {
    const { data } = await api.post("/auth/login/reset-session", { reset_token: resetToken });
    if (data.access_token) { localStorage.setItem("reason_token", data.access_token); window.dispatchEvent(new Event("reason-auth-changed")); }
    if (data.user) {
      const gen = ++genRef.current;
      setUser(data.user);
      setStatus("authed");
      (async () => {
        try {
          const [meRes, permRes] = await Promise.allSettled([
            api.get("/auth/me"),
            api.get("/security/me/permissions"),
          ]);
          if (gen !== genRef.current) return;
          if (meRes.status === "fulfilled") {
            setUser(meRes.value.data.user);
            setTenant(meRes.value.data.tenant);
          }
          setPermissions(permRes.status === "fulfilled" ? (permRes.value.data?.permissions || []) : null);
        } catch { /* backend enforces on each API call */ }
      })();
      return { user: data.user };
    }
    const ok = await fetchMe();
    if (!ok) throw new Error("We couldn't establish your session. Please try signing in again.");
    return { user: data.user };
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.access_token) { localStorage.setItem("reason_token", data.access_token); window.dispatchEvent(new Event("reason-auth-changed")); }
    await fetchMe();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    genRef.current++; // invalidate any in-flight auth checks
    localStorage.removeItem("reason_token");
    localStorage.removeItem("reason_tenant_override");
    // Perf cache: drop any per-user cached navigation menus so the next user never sees stale menus.
    try { Object.keys(localStorage).filter((k) => k.startsWith("reason_nav_v1_")).forEach((k) => localStorage.removeItem(k)); } catch (_) {}
    setUser(null);
    setTenant(null);
    setPermissions(null);
    setStatus("guest");
  };

  // Fine-grained permission check mirroring backend core/rbac._matches.
  // Returns true when permissions are unknown (fail-open) since the backend
  // still enforces the actual API call.
  const hasPermission = (code) => {
    if (!code) return true;
    if (permissions == null) return true;
    if (permissions.includes("*")) return true;
    return permissions.some((g) => matchPerm(g, code));
  };

  return (
    <AuthContext.Provider value={{ user, tenant, permissions, hasPermission, status, login, verifyMfaLogin, resetSession, register, logout, refresh: fetchMe, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

function matchPerm(granted, code) {
  if (granted === "*" || granted === code) return true;
  const [gMod, gAct] = granted.split(".");
  const [cMod, cAct] = code.split(".");
  if (gMod === cMod && gAct === "*") return true;
  if (gMod === "*" && gAct === cAct) return true;
  return false;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};
